/**
 * FikoSPHAnaliz - SPH Simülasyon Analiz Motoru
 *
 * Simülasyon metriklerini izler, enerji hesaplamaları yapar,
 * dolum analizi ve performans takibi sağlar.
 *
 * @author FikoYT CAD System
 * @version 1.0.0
 */

export interface SimulasyonIstatistik {
  dolumYuzdesi: number;          // 0-100
  aktifParcacik: number;
  toplamSpawn: number;
  toplamOlen: number;
  ortalamaHiz: number;           // m/s
  maxHiz: number;
  ortalamaBasinc: number;        // Pa
  maxBasinc: number;
  toplamKinetikEnerji: number;   // Joule
  toplamPotansiyelEnerji: number;
  fps: number;
  gpuSuresiMs: number;
  simZaman: number;              // seconds
  tahminiDolumSuresi: number;    // estimated fill time
}

export interface HeatmapConfig {
  aktif: boolean;
  tipi: number;    // 0-7 overlay types
  minDeger: number;
  maxDeger: number;
  otomatikAralik: boolean;
}

export class FikoSPHAnaliz {
  private istatistikler: SimulasyonIstatistik;
  private istatistikGecmisi: SimulasyonIstatistik[];
  private maxGecmisBoyut: number;
  private heatmapConfig: HeatmapConfig;

  // Frame timing
  private sonFrameZamani: number;
  private frameZamanlari: number[];
  private maxFrameZamanlari: number;

  // Fill rate tracking
  private oncekiDolumYuzdesi: number;
  private oncekiDolumZamani: number;
  private dolumHizlari: number[]; // percentage per second

  constructor() {
    this.istatistikler = {
      dolumYuzdesi: 0,
      aktifParcacik: 0,
      toplamSpawn: 0,
      toplamOlen: 0,
      ortalamaHiz: 0,
      maxHiz: 0,
      ortalamaBasinc: 0,
      maxBasinc: 0,
      toplamKinetikEnerji: 0,
      toplamPotansiyelEnerji: 0,
      fps: 60,
      gpuSuresiMs: 0,
      simZaman: 0,
      tahminiDolumSuresi: 0
    };

    this.istatistikGecmisi = [];
    this.maxGecmisBoyut = 3600; // 1 minute at 60fps
    this.maxFrameZamanlari = 60;

    this.heatmapConfig = {
      aktif: false,
      tipi: 0,
      minDeger: 0,
      maxDeger: 1,
      otomatikAralik: true
    };

    this.sonFrameZamani = performance.now();
    this.frameZamanlari = [];

    this.oncekiDolumYuzdesi = 0;
    this.oncekiDolumZamani = 0;
    this.dolumHizlari = [];
  }

  /**
   * GPU readback verisi ile istatistikleri güncelle
   * Asenkron, ~10Hz hızında çağrılır
   */
  guncelleIstatistikler(data: {
    activeCount: number,
    maxParticle: number,
    totalSpawned: number,
    totalDied: number,
    maxSpeed: number,
    simTime: number,
    avgSpeed?: number,
    avgPressure?: number,
    maxPressure?: number
  }): void {
    this.istatistikler.aktifParcacik = data.activeCount;
    this.istatistikler.toplamSpawn = data.totalSpawned;
    this.istatistikler.toplamOlen = data.totalDied;
    this.istatistikler.maxHiz = data.maxSpeed;
    this.istatistikler.simZaman = data.simTime;

    // Optional data
    if (data.avgSpeed !== undefined) {
      this.istatistikler.ortalamaHiz = data.avgSpeed;
    }
    if (data.avgPressure !== undefined) {
      this.istatistikler.ortalamaBasinc = data.avgPressure;
    }
    if (data.maxPressure !== undefined) {
      this.istatistikler.maxBasinc = data.maxPressure;
    }

    // Dolum yüzdesi hesapla
    this.istatistikler.dolumYuzdesi = this.hesaplaDolumYuzdesi(
      data.activeCount,
      data.maxParticle
    );

    // Dolum hızı ve tahmini süre
    const simdikiZaman = performance.now() / 1000;
    if (this.oncekiDolumZamani > 0) {
      const deltaZaman = simdikiZaman - this.oncekiDolumZamani;
      if (deltaZaman > 0) {
        const deltaDolum = this.istatistikler.dolumYuzdesi - this.oncekiDolumYuzdesi;
        const dolumHizi = deltaDolum / deltaZaman;

        this.dolumHizlari.push(dolumHizi);
        if (this.dolumHizlari.length > 30) {
          this.dolumHizlari.shift();
        }

        // Ortalama dolum hızı
        const ortDolumHizi = this.dolumHizlari.reduce((a, b) => a + b, 0) / this.dolumHizlari.length;
        this.istatistikler.tahminiDolumSuresi = this.tahminiDolumSuresi(
          this.istatistikler.dolumYuzdesi,
          ortDolumHizi
        );
      }
    }

    this.oncekiDolumYuzdesi = this.istatistikler.dolumYuzdesi;
    this.oncekiDolumZamani = simdikiZaman;

    // Geçmişe ekle
    this.istatistikGecmisi.push({ ...this.istatistikler });
    if (this.istatistikGecmisi.length > this.maxGecmisBoyut) {
      this.istatistikGecmisi.shift();
    }
  }

  /**
   * Her frame sonunda FPS takibi için çağrılır
   */
  frameTamamlandi(gpuMs?: number): void {
    const simdikiZaman = performance.now();
    const deltaZaman = simdikiZaman - this.sonFrameZamani;

    this.frameZamanlari.push(deltaZaman);
    if (this.frameZamanlari.length > this.maxFrameZamanlari) {
      this.frameZamanlari.shift();
    }

    // FPS hesapla (son N frame'in ortalaması)
    const ortDeltaZaman = this.frameZamanlari.reduce((a, b) => a + b, 0) / this.frameZamanlari.length;
    this.istatistikler.fps = ortDeltaZaman > 0 ? 1000 / ortDeltaZaman : 60;

    if (gpuMs !== undefined) {
      this.istatistikler.gpuSuresiMs = gpuMs;
    }

    this.sonFrameZamani = simdikiZaman;
  }

  /**
   * Güncel istatistikleri döndür
   */
  getIstatistikler(): SimulasyonIstatistik {
    return { ...this.istatistikler };
  }

  /**
   * İstatistik geçmişini döndür
   */
  getGecmis(): SimulasyonIstatistik[] {
    return [...this.istatistikGecmisi];
  }

  /**
   * Heatmap yapılandırmasını ayarla
   */
  setHeatmap(config: Partial<HeatmapConfig>): void {
    this.heatmapConfig = { ...this.heatmapConfig, ...config };
  }

  /**
   * Heatmap yapılandırmasını döndür
   */
  getHeatmap(): HeatmapConfig {
    return { ...this.heatmapConfig };
  }

  /**
   * Parçacık verilerinden enerji hesapla
   */
  hesaplaEnerji(
    positions: Float32Array,
    velocities: Float32Array,
    masses: Float32Array,
    gravity: { x: number, y: number, z: number }
  ): { kinetik: number, potansiyel: number } {
    let toplamKinetik = 0;
    let toplamPotansiyel = 0;

    const parcacikSayisi = Math.floor(positions.length / 4);

    for (let i = 0; i < parcacikSayisi; i++) {
      const i4 = i * 4;

      // Hız bileşenleri
      const vx = velocities[i4];
      const vy = velocities[i4 + 1];
      const vz = velocities[i4 + 2];
      const vMagnitude = Math.sqrt(vx * vx + vy * vy + vz * vz);

      // Konum
      const y = positions[i4 + 1];

      // Kütle (varsayılan 1.0)
      const m = masses ? masses[i] : 1.0;

      // Kinetik enerji: KE = 0.5 * m * v²
      toplamKinetik += 0.5 * m * vMagnitude * vMagnitude;

      // Potansiyel enerji: PE = m * g * h
      const gMagnitude = Math.sqrt(gravity.x * gravity.x + gravity.y * gravity.y + gravity.z * gravity.z);
      toplamPotansiyel += m * gMagnitude * y;
    }

    this.istatistikler.toplamKinetikEnerji = toplamKinetik;
    this.istatistikler.toplamPotansiyelEnerji = toplamPotansiyel;

    return {
      kinetik: toplamKinetik,
      potansiyel: toplamPotansiyel
    };
  }

  /**
   * Dolum yüzdesini hesapla
   */
  hesaplaDolumYuzdesi(activeCount: number, maxParticle: number): number {
    if (maxParticle === 0) return 0;
    return Math.min(100, (activeCount / maxParticle) * 100);
  }

  /**
   * Tahmini dolum süresini hesapla (saniye)
   */
  tahminiDolumSuresi(currentFill: number, fillRate: number): number {
    if (fillRate <= 0 || currentFill >= 100) {
      return 0;
    }

    const kalanYuzde = 100 - currentFill;
    return kalanYuzde / fillRate;
  }

  /**
   * Tüm verileri sıfırla
   */
  sifirla(): void {
    this.istatistikler = {
      dolumYuzdesi: 0,
      aktifParcacik: 0,
      toplamSpawn: 0,
      toplamOlen: 0,
      ortalamaHiz: 0,
      maxHiz: 0,
      ortalamaBasinc: 0,
      maxBasinc: 0,
      toplamKinetikEnerji: 0,
      toplamPotansiyelEnerji: 0,
      fps: 60,
      gpuSuresiMs: 0,
      simZaman: 0,
      tahminiDolumSuresi: 0
    };

    this.istatistikGecmisi = [];
    this.frameZamanlari = [];
    this.dolumHizlari = [];
    this.oncekiDolumYuzdesi = 0;
    this.oncekiDolumZamani = 0;
    this.sonFrameZamani = performance.now();
  }

  /**
   * JSON formatında dışa aktar
   */
  exportJSON(): string {
    return JSON.stringify({
      guncel: this.istatistikler,
      gecmis: this.istatistikGecmisi,
      heatmap: this.heatmapConfig
    }, null, 2);
  }

  /**
   * CSV formatında dışa aktar
   */
  exportCSV(): string {
    const headers = [
      'Zaman(s)',
      'Dolum(%)',
      'AktifParcacik',
      'ToplamSpawn',
      'ToplamOlen',
      'OrtHiz(m/s)',
      'MaxHiz(m/s)',
      'OrtBasinc(Pa)',
      'MaxBasinc(Pa)',
      'KinetikEnerji(J)',
      'PotansiyelEnerji(J)',
      'FPS',
      'GPUSure(ms)',
      'TahminiDolumSure(s)'
    ].join(',');

    const rows = this.istatistikGecmisi.map(stat => {
      return [
        stat.simZaman.toFixed(3),
        stat.dolumYuzdesi.toFixed(2),
        stat.aktifParcacik,
        stat.toplamSpawn,
        stat.toplamOlen,
        stat.ortalamaHiz.toFixed(3),
        stat.maxHiz.toFixed(3),
        stat.ortalamaBasinc.toFixed(2),
        stat.maxBasinc.toFixed(2),
        stat.toplamKinetikEnerji.toFixed(4),
        stat.toplamPotansiyelEnerji.toFixed(4),
        stat.fps.toFixed(1),
        stat.gpuSuresiMs.toFixed(2),
        stat.tahminiDolumSuresi.toFixed(1)
      ].join(',');
    });

    return [headers, ...rows].join('\n');
  }

  /**
   * Maksimum geçmiş boyutunu ayarla
   */
  setMaxGecmisBoyut(boyut: number): void {
    this.maxGecmisBoyut = Math.max(60, boyut);
    while (this.istatistikGecmisi.length > this.maxGecmisBoyut) {
      this.istatistikGecmisi.shift();
    }
  }

  /**
   * İstatistik özetini al (ortalamalar, min/max)
   */
  getOzet(): {
    ortalamaFPS: number;
    ortalamaGPUSure: number;
    maxAktifParcacik: number;
    toplamSimZaman: number;
    ortalamaDolumYuzdesi: number;
  } {
    if (this.istatistikGecmisi.length === 0) {
      return {
        ortalamaFPS: 0,
        ortalamaGPUSure: 0,
        maxAktifParcacik: 0,
        toplamSimZaman: 0,
        ortalamaDolumYuzdesi: 0
      };
    }

    const toplamFPS = this.istatistikGecmisi.reduce((sum, s) => sum + s.fps, 0);
    const toplamGPU = this.istatistikGecmisi.reduce((sum, s) => sum + s.gpuSuresiMs, 0);
    const toplamDolum = this.istatistikGecmisi.reduce((sum, s) => sum + s.dolumYuzdesi, 0);
    const maxParcacik = Math.max(...this.istatistikGecmisi.map(s => s.aktifParcacik));
    const sonSimZaman = this.istatistikGecmisi[this.istatistikGecmisi.length - 1].simZaman;

    const n = this.istatistikGecmisi.length;

    return {
      ortalamaFPS: toplamFPS / n,
      ortalamaGPUSure: toplamGPU / n,
      maxAktifParcacik: maxParcacik,
      toplamSimZaman: sonSimZaman,
      ortalamaDolumYuzdesi: toplamDolum / n
    };
  }
}
